import { Link } from "react-router-dom";
import { ArrowRight, Phone, Check, MessageCircle } from "lucide-react";
import SEO from "@/components/SEO";
import CtaFinal from "@/components/CtaFinal";
import { Reveal, MaskedLineInView } from "@/components/Reveal";
import { PHONE_TEL, WHATSAPP_URL } from "@/data/content";

const INCLUDE_HEADINGS = {
  "ristrutturazioni-complete": "Tutto quello che serve, incluso.",
  "opere-murarie": "Lavorazioni murarie, nel dettaglio.",
  "rifacimento-bagni": "Cosa facciamo nel tuo bagno.",
  "rifacimento-cucine": "Come prepariamo l'ambiente cucina.",
  "pavimenti-rivestimenti": "Dalla rimozione alla posa.",
  "cartongesso-controsoffitti": "Le forme che realizziamo.",
  "tinteggiature-finiture": "Colori e finiture, nel dettaglio.",
  "esterni": "Dove interveniamo all'esterno.",
  "manutenzioni-edili": "Interventi grandi e piccoli.",
};

const STEP_HEADINGS = {
  "ristrutturazioni-complete": "Quattro mosse, un programma chiaro.",
  "opere-murarie": "Come si apre e si chiude un cantiere murario.",
  "rifacimento-bagni": "Il bagno nuovo in quattro fasi.",
  "rifacimento-cucine": "Prima il cantiere, poi il montaggio.",
  "pavimenti-rivestimenti": "Dal fondo alla fuga.",
  "cartongesso-controsoffitti": "Dal disegno alla rasatura.",
  "tinteggiature-finiture": "Preparare, colorare, controllare.",
  "esterni": "Lavorare all'esterno, con ordine.",
  "manutenzioni-edili": "Veloci, chiari, verificati.",
};

const CTA_BUTTONS = {
  "ristrutturazioni-complete": "Parliamo del tuo progetto",
  "opere-murarie": "Raccontaci cosa vuoi realizzare",
  "rifacimento-bagni": "Valutiamo insieme il tuo bagno",
  "rifacimento-cucine": "Contattaci per la tua cucina",
  "pavimenti-rivestimenti": "Richiedi informazioni",
  "cartongesso-controsoffitti": "Hai un progetto? Parliamone",
  "tinteggiature-finiture": "Valutiamo insieme l'intervento",
  "esterni": "Parliamo dei tuoi esterni",
  "manutenzioni-edili": "Richiedi un intervento",
};

const BENEFITS_EYEBROW = { wide: "Vantaggi per te", split: "Perché conviene", editorial: "Cosa ci guadagni" };

const HeroCtas = ({ service }) => {
  const { slug, variant } = service;
  return (
    <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4">
      <Link
        to="/contatti"
        data-testid={`${slug}-cta-sopralluogo`}
        className="group inline-flex items-center justify-center gap-3 bg-terracotta hover:bg-terracotta-hover text-ivory font-display text-sm tracking-[0.15em] uppercase px-8 py-4 transition-colors duration-300"
      >
        Richiedi un sopralluogo
        <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
      {variant === "split" ? (
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${slug}-cta-whatsapp`}
          className="inline-flex items-center justify-center gap-3 border border-current/40 font-display text-sm tracking-[0.15em] uppercase px-8 py-4 opacity-80 hover:opacity-100 transition-opacity duration-300"
        >
          <MessageCircle size={16} strokeWidth={1.5} />
          Scrivici su WhatsApp
        </a>
      ) : (
        <a
          href={PHONE_TEL}
          data-testid={`${slug}-cta-call`}
          className="inline-flex items-center justify-center gap-3 border border-current/40 font-display text-sm tracking-[0.15em] uppercase px-8 py-4 opacity-80 hover:opacity-100 transition-opacity duration-300"
        >
          <Phone size={16} strokeWidth={1.5} />
          Chiama ora
        </a>
      )}
    </div>
  );
};

const MidCta = ({ service }) => (
  <Reveal>
    <div data-testid={`${service.slug}-mid-cta`} className="bg-terracotta text-ivory px-8 md:px-14 py-12 md:py-16 flex flex-col md:flex-row md:items-center justify-between gap-8 grain relative">
      <h2 className="font-display text-2xl md:text-4xl tracking-tight max-w-xl">{service.ctaLabel}</h2>
      <Link
        to="/contatti"
        data-testid={`${service.slug}-mid-cta-button`}
        className="group inline-flex items-center justify-center gap-3 bg-ivory text-anthracite hover:bg-panna font-display text-sm tracking-[0.15em] uppercase px-8 py-4 shrink-0 transition-colors duration-300"
      >
        {CTA_BUTTONS[service.slug]}
        <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    </div>
  </Reveal>
);

const Benefits = ({ service, dark }) => (
  <section className={dark ? "bg-carbone text-ivory grain relative" : "bg-sand/60"}>
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
      <Reveal>
        <p className={`font-display text-xs tracking-[0.3em] uppercase mb-10 flex items-center gap-4 ${dark ? "text-ivory/50" : "text-concrete"}`}>
          <span className="text-terracotta">04</span>
          <span className={`h-px w-8 ${dark ? "bg-ivory/30" : "bg-anthracite/20"}`} />
          {BENEFITS_EYEBROW[service.variant]}
        </p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
        {service.benefits.map((b, i) => (
          <Reveal key={b.t} delay={i * 0.1}>
            <div data-testid={`${service.slug}-benefit-${i + 1}`} className={`border-t-2 border-terracotta pt-6 ${dark ? "" : ""}`}>
              <h3 className={`font-display text-xl md:text-2xl tracking-tight ${dark ? "text-ivory" : "text-anthracite"}`}>{b.t}</h3>
              <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-ivory/60" : "text-concrete"}`}>{b.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Steps = ({ service, layout }) => (
  <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
    <Reveal>
      <p className="font-display text-xs tracking-[0.3em] uppercase text-concrete mb-4 flex items-center gap-4">
        <span className="text-terracotta">03</span>
        <span className="h-px w-8 bg-anthracite/20" />
        Come viene svolto
      </p>
    </Reveal>
    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-anthracite max-w-2xl mb-12 md:mb-16">
      {STEP_HEADINGS[service.slug]}
    </h2>
    {layout === "timeline" ? (
      <ol className="relative max-w-2xl">
        <div className="absolute left-[19px] top-0 bottom-0 w-px bg-anthracite/15" aria-hidden="true" />
        {service.steps.map((s, i) => (
          <Reveal key={s.t} delay={i * 0.05}>
            <li data-testid={`${service.slug}-step-${i + 1}`} className="relative flex gap-6 pb-10 last:pb-0">
              <span className="shrink-0 w-10 h-10 bg-terracotta text-ivory font-display text-sm flex items-center justify-center z-10">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="pt-1.5">
                <h3 className="font-display text-xl tracking-tight text-anthracite">{s.t}</h3>
                <p className="mt-2 text-sm text-concrete leading-relaxed">{s.d}</p>
              </div>
            </li>
          </Reveal>
        ))}
      </ol>
    ) : (
      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-anthracite/10">
        {service.steps.map((s, i) => (
          <Reveal key={s.t} delay={i * 0.07}>
            <li data-testid={`${service.slug}-step-${i + 1}`} className="border-b border-r border-anthracite/10 p-7 h-full bg-panna/40">
              <p className="font-display text-terracotta text-sm tracking-[0.2em]">{String(i + 1).padStart(2, "0")}</p>
              <h3 className="mt-3 font-display text-lg tracking-tight text-anthracite">{s.t}</h3>
              <p className="mt-2 text-sm text-concrete leading-relaxed">{s.d}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    )}
  </section>
);

const WideHero = ({ service }) => (
  <section data-testid={`${service.slug}-hero`} className="relative min-h-[88svh] flex items-end overflow-hidden bg-carbone">
    <img src={service.heroImg} alt={service.heroAlt} className="absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
    <div className="absolute inset-0 bg-gradient-to-t from-carbone via-carbone/50 to-carbone/20" />
    <div className="relative z-10 max-w-7xl mx-auto w-full px-5 md:px-8 pt-36 pb-14 md:pb-20 text-ivory">
      <Reveal>
        <p className="font-display text-[11px] md:text-xs tracking-[0.35em] uppercase text-ivory/70 mb-6 flex items-center gap-4">
          <span className="h-px w-10 bg-terracotta" />
          Servizi Sopaj
        </p>
      </Reveal>
      <h1 className="font-display font-medium text-[11vw] sm:text-5xl lg:text-7xl tracking-tight leading-[1.02]">
        <MaskedLineInView>{service.title}</MaskedLineInView>
        <MaskedLineInView delay={0.12}>
          <span className="font-serif italic font-normal text-ivory/95">{service.claim}</span>
        </MaskedLineInView>
      </h1>
      <Reveal delay={0.2}>
        <p className="mt-6 max-w-xl text-ivory/75 text-base md:text-lg leading-relaxed">{service.intro}</p>
      </Reveal>
      <Reveal delay={0.3}><HeroCtas service={service} /></Reveal>
    </div>
  </section>
);

const SplitHero = ({ service }) => (
  <section data-testid={`${service.slug}-hero`} className="max-w-7xl mx-auto px-5 md:px-8 pt-32 md:pt-44 pb-16 md:pb-24">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-6 lg:sticky lg:top-32">
        <Reveal>
          <p className="font-display text-[11px] md:text-xs tracking-[0.35em] uppercase text-concrete mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-terracotta" />
            Servizi Sopaj
          </p>
        </Reveal>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-anthracite">
          <MaskedLineInView>{service.title}</MaskedLineInView>
          <MaskedLineInView delay={0.12}>
            <span className="font-serif italic font-normal">{service.claim}</span>
          </MaskedLineInView>
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-7 text-anthracite/75 leading-relaxed text-base md:text-lg max-w-lg">{service.intro}</p>
        </Reveal>
        <Reveal delay={0.3}><HeroCtas service={service} /></Reveal>
      </div>
      <div className="lg:col-span-6">
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="absolute -top-5 -left-5 w-full h-full border border-terracotta/40" aria-hidden="true" />
            <img src={service.heroImg} alt={service.heroAlt} className="relative w-full aspect-[4/3] object-cover" fetchpriority="high" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const EditorialHero = ({ service }) => (
  <section data-testid={`${service.slug}-hero`} className="pt-32 md:pt-44 pb-14 md:pb-20">
    <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
      <Reveal>
        <p className="font-display text-[11px] md:text-xs tracking-[0.35em] uppercase text-concrete mb-6 flex items-center justify-center gap-4">
          <span className="h-px w-10 bg-terracotta" />
          Servizi Sopaj
          <span className="h-px w-10 bg-terracotta" />
        </p>
      </Reveal>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-anthracite">
        <MaskedLineInView>{service.title}</MaskedLineInView>
        <MaskedLineInView delay={0.12}>
          <span className="font-serif italic font-normal">{service.claim}</span>
        </MaskedLineInView>
      </h1>
      <Reveal delay={0.2}>
        <p className="mt-7 text-anthracite/75 leading-relaxed text-base md:text-lg max-w-2xl mx-auto">{service.intro}</p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="flex justify-center"><HeroCtas service={service} /></div>
      </Reveal>
    </div>
    <Reveal delay={0.2}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 mt-14 md:mt-20">
        <img src={service.heroImg} alt={service.heroAlt} className="w-full aspect-[16/8] object-cover" fetchpriority="high" />
      </div>
    </Reveal>
  </section>
);

const IncludesGrid = ({ service }) => (
  <section className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28">
    <Reveal>
      <p className="font-display text-xs tracking-[0.3em] uppercase text-concrete mb-4 flex items-center gap-4">
        <span className="text-terracotta">02</span>
        <span className="h-px w-8 bg-anthracite/20" />
        Cosa comprende
      </p>
    </Reveal>
    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-anthracite max-w-2xl mb-12 md:mb-16">
      {INCLUDE_HEADINGS[service.slug]}
    </h2>
    <ul className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-anthracite/10">
      {service.includes.map((item, i) => (
        <Reveal key={item} delay={(i % 2) * 0.07}>
          <li data-testid={`${service.slug}-include-${i + 1}`} className="border-b border-r border-anthracite/10 p-6 md:p-8 flex items-start gap-4 bg-panna/40 h-full">
            <p className="font-display text-terracotta text-sm tracking-[0.2em] shrink-0">{String(i + 1).padStart(2, "0")}</p>
            <p className="font-display text-lg tracking-tight text-anthracite">{item}</p>
          </li>
        </Reveal>
      ))}
    </ul>
  </section>
);

const IncludesChecks = ({ service }) => (
  <section className="bg-sand/60">
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <Reveal>
          <p className="font-display text-xs tracking-[0.3em] uppercase text-concrete mb-4 flex items-center gap-4">
            <span className="text-terracotta">02</span>
            <span className="h-px w-8 bg-anthracite/20" />
            Cosa comprende
          </p>
        </Reveal>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-anthracite">
          {INCLUDE_HEADINGS[service.slug]}
        </h2>
        <Reveal delay={0.15}>
          <img src={service.gallery[0].src} alt={service.gallery[0].alt} className="mt-10 w-full aspect-[4/3] object-cover" loading="lazy" />
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <ul className="divide-y divide-anthracite/10 border-y border-anthracite/10">
          {service.includes.map((item, i) => (
            <Reveal key={item} delay={i * 0.05}>
              <li data-testid={`${service.slug}-include-${i + 1}`} className="flex items-start gap-4 py-5">
                <Check size={18} strokeWidth={2} className="text-terracotta mt-1 shrink-0" />
                <p className="font-display text-lg md:text-xl tracking-tight text-anthracite">{item}</p>
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const GalleryDuo = ({ service, offset }) => (
  <section className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-14">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
      {service.gallery.map((g, i) => (
        <Reveal key={g.src} delay={i * 0.12}>
          <img
            src={g.src}
            alt={g.alt}
            loading="lazy"
            className={`w-full object-cover ${offset && i === 1 ? "md:mt-16 aspect-[4/3]" : "aspect-[4/3]"}`}
          />
        </Reveal>
      ))}
    </div>
  </section>
);

const ServicePage = ({ service }) => {
  const { variant } = service;
  return (
    <>
      <SEO title={service.seoTitle} description={service.seoDescription} path={`/${service.slug}`} />

      {variant === "wide" && <WideHero service={service} />}
      {variant === "split" && <SplitHero service={service} />}
      {variant === "editorial" && <EditorialHero service={service} />}

      {variant === "wide" && (
        <>
          <IncludesGrid service={service} />
          <GalleryDuo service={service} offset />
          <Steps service={service} layout="grid" />
          <div className="max-w-7xl mx-auto px-5 md:px-8 pb-4"><MidCta service={service} /></div>
          <Benefits service={service} dark />
        </>
      )}

      {variant === "split" && (
        <>
          <IncludesChecks service={service} />
          <Steps service={service} layout="timeline" />
          <GalleryDuo service={service} offset={false} />
          <Benefits service={service} dark={false} />
          <div className="max-w-7xl mx-auto px-5 md:px-8 py-16"><MidCta service={service} /></div>
        </>
      )}

      {variant === "editorial" && (
        <>
          <GalleryDuo service={service} offset />
          <IncludesGrid service={service} />
          <div className="max-w-7xl mx-auto px-5 md:px-8 pb-4"><MidCta service={service} /></div>
          <Steps service={service} layout="timeline" />
          <Benefits service={service} dark />
        </>
      )}

      <CtaFinal />
    </>
  );
};

export default ServicePage;
